import React, { useState, useEffect } from "react";
import { FiX, FiCheck, FiAlertCircle } from "react-icons/fi";
import { getCustomerLeadPurposes, addCustomerLead, editCustomerLead } from "../../../api/customer/customerFeatureApis";

const AddEditCustomerLeadModal = ({ isOpen, onClose, onSuccess, initialLead = null, token }) => {
  const isEdit = Boolean(initialLead && initialLead.id);

  const [purposes, setPurposes] = useState([]);
  const [loadingPurposes, setLoadingPurposes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    budget: "",
    state: "",
    city: "",
    purposeId: "",
  });

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;

    if (initialLead) {
      setFormData({
        title: initialLead.title || "",
        description: initialLead.description || "",
        budget: initialLead.budget ? String(initialLead.budget) : "",
        state: initialLead.state || "",
        city: initialLead.city || "",
        purposeId: initialLead.purpose?.id || initialLead.purposeId || "",
      });
    } else {
      setFormData({
        title: "",
        description: "",
        budget: "",
        state: "",
        city: "",
        purposeId: "",
      });
    }

    setValidationErrors({});
    setError(null);

    // Fetch purposes
    let isMounted = true;
    setLoadingPurposes(true);
    getCustomerLeadPurposes(token)
      .then((data) => {
        if (!isMounted) return;
        const list = data?.customerLeadPurposes || [];
        setPurposes(list);
        if (!initialLead && list.length > 0) {
          setFormData((prev) => ({ ...prev, purposeId: prev.purposeId || String(list[0].id) }));
        }
      })
      .catch(() => {
        if (isMounted) setError("Could not load lead purposes. Please try again.");
      })
      .finally(() => {
        if (isMounted) setLoadingPurposes(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, initialLead, token]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.title || formData.title.trim().length < 5) {
      errors.title = "Title must be at least 5 characters long";
    }
    if (!formData.description || formData.description.trim().length === 0) {
      errors.description = "Description is required";
    }
    if (!formData.budget || isNaN(Number(formData.budget)) || Number(formData.budget) <= 0) {
      errors.budget = "Please enter a valid positive numeric budget";
    }
    if (!formData.state || formData.state.trim().length === 0) {
      errors.state = "State is required";
    }
    if (!formData.city || formData.city.trim().length === 0) {
      errors.city = "City is required";
    }
    if (!formData.purposeId) {
      errors.purposeId = "Please select a purpose";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setError(null);

    const payload = {
      ...(isEdit && { id: Number(initialLead.id) }),
      title: formData.title.trim(),
      description: formData.description.trim(),
      budget: Number(formData.budget),
      state: formData.state.trim(),
      city: formData.city.trim(),
      purpose: {
        id: Number(formData.purposeId),
      },
    };

    try {
      const response = isEdit
        ? await editCustomerLead(token, payload)
        : await addCustomerLead(token, payload);

      if (response?.statusType === "SUCCESS" || response?.customerLead) {
        onSuccess(response?.customerLead || payload);
        onClose();
      } else {
        setError(response?.text || "Failed to save customer lead");
      }
    } catch (err) {
      setError(err?.response?.data?.text || err?.message || "An error occurred while saving the lead");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="customer-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="lead-modal-title">
      <div className="customer-modal">
        <div className="customer-modal__header">
          <h2 id="lead-modal-title" className="customer-modal__title">
            {isEdit ? "Edit Customer Lead" : "Post New Requirement Lead"}
          </h2>
          <button
            type="button"
            className="btn btn-sm btn-light border-0"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="customer-modal__body">
            {error && (
              <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-0" role="alert">
                <FiAlertCircle className="flex-shrink-0" />
                <span className="small">{error}</span>
              </div>
            )}

            <div className="customer-form-group">
              <label htmlFor="lead-title" className="customer-form-label">
                Requirement Title <span className="required">*</span>
              </label>
              <input
                id="lead-title"
                name="title"
                type="text"
                className="customer-form-input"
                placeholder="e.g. Looking for 3 BHK apartment in Noida Sector 62"
                value={formData.title}
                onChange={handleChange}
                disabled={submitting}
              />
              {validationErrors.title && (
                <span className="customer-form-error">{validationErrors.title}</span>
              )}
            </div>

            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="customer-form-group">
                  <label htmlFor="lead-purpose" className="customer-form-label">
                    Purpose / Requirement Type <span className="required">*</span>
                  </label>
                  <select
                    id="lead-purpose"
                    name="purposeId"
                    className="customer-form-select"
                    value={formData.purposeId}
                    onChange={handleChange}
                    disabled={submitting || loadingPurposes}
                  >
                    <option value="">Select Purpose</option>
                    {purposes.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name || p.purpose_name || `Purpose #${p.id}`}
                      </option>
                    ))}
                  </select>
                  {validationErrors.purposeId && (
                    <span className="customer-form-error">{validationErrors.purposeId}</span>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="customer-form-group">
                  <label htmlFor="lead-budget" className="customer-form-label">
                    Estimated Budget (₹) <span className="required">*</span>
                  </label>
                  <input
                    id="lead-budget"
                    name="budget"
                    type="number"
                    min="1"
                    className="customer-form-input"
                    placeholder="e.g. 7500000"
                    value={formData.budget}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                  {validationErrors.budget && (
                    <span className="customer-form-error">{validationErrors.budget}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="customer-form-group">
                  <label htmlFor="lead-state" className="customer-form-label">
                    State <span className="required">*</span>
                  </label>
                  <input
                    id="lead-state"
                    name="state"
                    type="text"
                    className="customer-form-input"
                    placeholder="e.g. Uttar Pradesh"
                    value={formData.state}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                  {validationErrors.state && (
                    <span className="customer-form-error">{validationErrors.state}</span>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="customer-form-group">
                  <label htmlFor="lead-city" className="customer-form-label">
                    City <span className="required">*</span>
                  </label>
                  <input
                    id="lead-city"
                    name="city"
                    type="text"
                    className="customer-form-input"
                    placeholder="e.g. Noida"
                    value={formData.city}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                  {validationErrors.city && (
                    <span className="customer-form-error">{validationErrors.city}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="customer-form-group">
              <label htmlFor="lead-description" className="customer-form-label">
                Detailed Description & Preferences <span className="required">*</span>
              </label>
              <textarea
                id="lead-description"
                name="description"
                rows="4"
                className="customer-form-textarea"
                placeholder="Describe your ideal property size, preferred possession date, amenities, or nearby landmarks..."
                value={formData.description}
                onChange={handleChange}
                disabled={submitting}
              />
              {validationErrors.description && (
                <span className="customer-form-error">{validationErrors.description}</span>
              )}
            </div>
          </div>

          <div className="customer-modal__footer">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm px-3"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm px-4 d-inline-flex align-items-center gap-2"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FiCheck size={16} />
                  <span>{isEdit ? "Update Lead" : "Post Requirement"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditCustomerLeadModal;
